var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Icon } from '@iconify/react';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Autocomplete, Box, Button, Checkbox, Chip, Drawer, FormControl, Grid, IconButton, Menu, MenuItem, Tab, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { CategoryScale, Chart as ChartJS, LinearScale, LineElement, PointElement, Tooltip } from 'chart.js';
import dayjs from 'dayjs';
import { CheckmarkCircle01Icon, InformationCircleIcon, MessageDelay02Icon, MessageDone02Icon, SortByUp01Icon, ThumbsDownIcon, ThumbsUpIcon } from 'hugeicons-react';
import { ArrowDown2 } from 'iconsax-reactjs';
import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppConsts } from '../../../application/common/constants/app-consts';
import { RoleCategories } from '../../../application/common/constants/role-categories';
import { getFilteredStringOperators } from '../../../application/common/helpers/grid-filter-operators';
import { useGetAuditDataQuery, useGetConversationsListQuery } from '../../../application/features/chat/chat-api';
import { useGetMachinesListQuery } from '../../../application/features/machines/machine-api';
import { useAppSelector } from '../../../application/store/useStore';
import useTranslate from '../../../localization/helpers';
import DateRangePicker from '../../components/date-range-picker';
import FilterButton from '../../components/FilterButton';
import useCurrentUser from '../../hooks/useCurrentUser';
import useFilters from '../../hooks/useFilters';
import AuditCards from './audit-cards';
import AuditDesktop from './audit-desktop';
ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Tooltip);
var Audit = function () {
    var _a;
    var navigate = useNavigate();
    var theme = useTheme();
    var currentUser = useAppSelector(function (state) { return state.auth; }).currentUser;
    var isSuperAdmin = currentUser.roleCategory === RoleCategories.SuperAdmin;
    var companyId = useCurrentUser().companyId;
    var translate = useTranslate().translate;
    var isXs = useMediaQuery(theme.breakpoints.only('xs'));
    var isSm = useMediaQuery(theme.breakpoints.only('sm'));
    var isMd = useMediaQuery(theme.breakpoints.only('md'));
    var isscreen600900 = useMediaQuery('(min-width:600px) and (max-width:900px)');
    var iscreen9001200 = useMediaQuery('(min-width:900px) and (max-width:1200px)');
    var isscreen9001200Landscape = useMediaQuery('(min-width:901px) and (max-width:1201px) and (orientation: landscape)');
    var isscreen600900Landscape = useMediaQuery('(min-width:600px) and (max-width:900px) and (orientation: landscape)');
    var isLg = useMediaQuery(theme.breakpoints.up('lg'));
    var isScreen12001300 = useMediaQuery('(min-width: 1200px) and (max-width: 1300px)');
    var isIpadMini = useMediaQuery('(max-width: 768px)');
    var isIpadMiniLandscape = useMediaQuery('(max-width: 1024px) and (orientation: landscape)');
    var _b = __read(useState(false), 2), openFilterDrawer = _b[0], setOpenFilterDrawer = _b[1];
    var handleFilterClick = function () {
        setOpenFilterDrawer(function (prev) { return !prev; });
    };
    var _c = __read(useState(false), 2), openSortDrawer = _c[0], setOpenSortDrawer = _c[1];
    var _d = __read(useState(null), 2), daysAnchorEl = _d[0], setDaysAnchorEl = _d[1];
    var _e = __read(useState('Audits'), 2), tabValue = _e[0], setTabValue = _e[1];
    var handleChange = function (_event, newValue) {
        setTabValue(newValue);
    };
    var _f = __read(useState({
        days: 7,
        dateRange: 'audit.days',
        startDate: dayjs().subtract(7, 'day').toDate(),
        endDate: dayjs().toDate(),
        machines: [],
        status: []
    }), 2), auditFilters = _f[0], setAuditFilters = _f[1];
    var _g = __read(useFilters({
        status: [],
        machines: [],
        startDate: dayjs().subtract(7, 'day').toDate(),
        endDate: dayjs().toDate(),
        page: AppConsts.DEFAULT_PAGE_NUMBER,
        pageSize: AppConsts.DEFAULT_PAGE_SIZE,
        sortField: AppConsts.DEFAULT_SORT_FIELD_MONGO,
        sortDirection: AppConsts.DEFAULT_SORT_DIRECTION,
        filterField: undefined,
        filterOperator: undefined,
        filterValue: undefined,
        companyId: companyId
    }, function () {
        setAuditFilters({
            startDate: dayjs().subtract(7, 'day').toDate(),
            endDate: dayjs().toDate(),
            days: 7,
            dateRange: 'audit.days',
            machines: [],
            status: []
        });
    }), 2), conversationFilters = _g[0], setConversationFilters = _g[1];
    var _h = useGetMachinesListQuery({
        filterByCompany: true,
        isMachineLibrary: false
    }).data, machinesList = _h === void 0 ? [] : _h;
    var auditData = useGetAuditDataQuery({
        startDate: conversationFilters.startDate,
        endDate: conversationFilters.endDate
    }, { refetchOnMountOrArgChange: true }).data;
    var data = useGetConversationsListQuery(conversationFilters, {
        refetchOnMountOrArgChange: true
    }).data;
    var _j = (data !== null && data !== void 0 ? data : {}).items, conversations = _j === void 0 ? [] : _j;
    var selectedDate = useMemo(function () {
        return [dayjs(auditFilters.startDate), dayjs(auditFilters.endDate)];
    }, [auditFilters]);
    var handleFilterModelChange = function (model) {
        if (model.items.length > 0) {
            var filterItem_1 = model.items[0];
            if (filterItem_1.operator !== 'isEmpty' &&
                filterItem_1.operator !== 'isNotEmpty' &&
                (filterItem_1.value === '' ||
                    (Array.isArray(filterItem_1.value) && filterItem_1.value.length === 0) ||
                    filterItem_1.value === undefined)) {
                setConversationFilters(function (prev) { return (__assign(__assign({}, prev), { filterField: undefined, filterOperator: undefined, filterValue: undefined })); });
            }
            else {
                setConversationFilters(function (prev) {
                    var _a;
                    return (__assign(__assign({}, prev), { filterField: filterItem_1.field, filterOperator: filterItem_1.operator, filterValue: (_a = filterItem_1.value) === null || _a === void 0 ? void 0 : _a.toString() }));
                });
            }
        }
        else {
            // When model.items is empty, clear all filters
            setConversationFilters(function (prev) { return (__assign(__assign({}, prev), { filterField: undefined, filterOperator: undefined, filterValue: undefined })); });
        }
        setFilterModel(model);
    };
    var _k = __read(useState({
        items: []
    }), 2), filterModel = _k[0], setFilterModel = _k[1];
    var filter = function () {
        setConversationFilters(function (prev) { return (__assign(__assign({}, prev), { startDate: auditFilters.startDate, endDate: auditFilters.endDate, machines: auditFilters.machines, status: auditFilters.status })); });
    };
    var rowCountRef = useRef((_a = data === null || data === void 0 ? void 0 : data.totalCount) !== null && _a !== void 0 ? _a : 0);
    var rowCount = useMemo(function () {
        if ((data === null || data === void 0 ? void 0 : data.totalCount) !== undefined) {
            rowCountRef.current = data === null || data === void 0 ? void 0 : data.totalCount;
        }
        return rowCountRef.current;
    }, [data]);
    var handlePaginationModelChange = function (model) {
        setConversationFilters(function (prev) { return (__assign(__assign({}, prev), { page: model.page + 1, pageSize: model.pageSize })); });
    };
    var handleSortModelChange = function (model) {
        setConversationFilters(function (prev) {
            var _a, _b, _c, _d;
            return (__assign(__assign({}, prev), { sortField: (_b = (_a = model[0]) === null || _a === void 0 ? void 0 : _a.field) !== null && _b !== void 0 ? _b : AppConsts.DEFAULT_SORT_FIELD_MONGO, sortDirection: (_d = (_c = model[0]) === null || _c === void 0 ? void 0 : _c.sort) !== null && _d !== void 0 ? _d : AppConsts.DEFAULT_SORT_DIRECTION }));
        });
    };
    var statusList = [
        { value: 'Active', label: translate('audit.active'), disabled: false },
        { value: 'Closed', label: translate('audit.completed'), disabled: false }
    ];
    var weekOptions = [
        { value: 7, label: 'audit.days' },
        { value: 10, label: 'audit.days' },
        { value: 14, label: 'audit.days' },
        { value: 30, label: 'audit.days' },
        { value: 45, label: 'audit.days' },
        { value: 60, label: 'audit.days' },
        { value: 90, label: 'audit.days' },
        { value: 183, label: 'audit.lastSixMonths' },
        { value: 365, label: 'audit.lastYear' },
        { value: 0, label: 'audit.customDate' }
    ];
    var onWeekChange = function (event) {
        return setDaysAnchorEl(event.currentTarget);
    };
    var onWeekClose = function (selectedValue, selectedLabel) {
        setAuditFilters(function (current) { return (__assign(__assign({}, current), { startDate: dayjs().subtract(selectedValue, 'day').toDate(), endDate: dayjs().toDate(), days: selectedValue, dateRange: selectedLabel })); });
        setConversationFilters(function (prev) { return (__assign(__assign({}, prev), { startDate: dayjs().subtract(selectedValue, 'day').toDate(), endDate: dayjs().toDate() })); });
        setDaysAnchorEl(null);
    };
    var handleAuditDetails = function (chat) {
        navigate("/audit/chat-details/".concat(chat.conversationId, "/").concat(chat.userId));
    };
    var options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            tooltip: {
                callbacks: {
                    title: function (tooltipItems) {
                        var date = tooltipItems[0].label;
                        return dayjs(date).format('YYYY-MM-DD');
                    }
                }
            },
            legend: {
                display: false
            }
        },
        scales: {
            x: {
                display: false
            },
            y: {
                display: false
            }
        }
    };
    var auditColumns = __spreadArray(__spreadArray([
        {
            field: 'conversationName',
            headerName: translate('audit.title'),
            flex: isXs ? undefined : isMd ? undefined : isscreen600900 ? undefined : isSm ? 1.5 : 1.5,
            minWidth: isXs ? 150 : isscreen600900 ? 150 : isMd ? 150 : undefined,
            width: isXs ? 200 : isMd ? 200 : isscreen600900 ? 200 : undefined,
            type: 'string',
            filterOperators: getFilteredStringOperators(),
            renderCell: function (params) {
                return (_jsx(Box, { display: 'flex', flexDirection: 'row', alignItems: 'center', children: _jsx(Typography, { style: {
                            whiteSpace: 'nowrap',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }, variant: 'body2', children: params.row.conversationName }) }));
            }
        },
        {
            field: 'date',
            headerName: translate('audit.date'),
            filterable: false,
            flex: isXs ? undefined : isMd ? undefined : isscreen600900 ? undefined : isSm ? 1.5 : 1.5,
            minWidth: isXs ? 150 : isscreen600900 ? 150 : isMd ? 150 : undefined,
            width: isXs ? 200 : isMd ? 200 : isscreen600900 ? 200 : undefined,
            type: 'string',
            renderCell: function (params) {
                return (_jsxs(Box, { children: [_jsx(Typography, { children: new Date(params.row.date).toLocaleDateString(translate('common.dateLanguageCode1'), {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                            }) }), _jsx(Typography, { variant: 'caption', color: 'textSecondary', children: new Date(params.row.date).toLocaleTimeString(translate('common.dateLanguageCode2'), {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true
                            }) })] }));
            }
        },
        {
            field: 'chatBy',
            headerName: translate('common.chatBy'),
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            flex: isXs ? undefined : isMd ? undefined : isscreen600900 ? undefined : isSm ? 1.5 : 1.5,
            minWidth: isXs ? 150 : isscreen600900 ? 150 : isMd ? 150 : undefined,
            width: isXs ? 200 : isMd ? 200 : isscreen600900 ? 200 : undefined,
            type: 'string'
        }
    ], __read((isSuperAdmin
        ? [
            {
                field: 'companyName',
                headerName: translate('common.company'),
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                flex: isXs
                    ? undefined
                    : isMd
                        ? undefined
                        : isscreen600900
                            ? undefined
                            : isSm
                                ? 1.5
                                : 1.5,
                minWidth: isXs ? 150 : isscreen600900 ? 150 : isMd ? 150 : undefined,
                width: isXs ? 200 : isMd ? 200 : isscreen600900 ? 200 : undefined,
                type: 'string',
                renderCell: function (params) {
                    return (_jsx(Box, { display: 'flex', flexDirection: 'row', alignItems: 'center', children: _jsx(Typography, { style: {
                                whiteSpace: 'nowrap',
                                maxWidth: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }, variant: 'body2', children: params.row.companyName }) }));
                }
            }
        ]
        : [])), false), [
        {
            field: 'machineName',
            headerName: translate('common.asset'),
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            flex: isXs ? undefined : isMd ? undefined : isscreen600900 ? undefined : isSm ? 1.5 : 1.5,
            minWidth: isXs ? 150 : isscreen600900 ? 150 : isMd ? 150 : undefined,
            width: isXs ? 200 : isMd ? 200 : isscreen600900 ? 200 : undefined,
            type: 'string',
            renderCell: function (params) {
                return (_jsx(Box, { display: 'flex', flexDirection: 'row', alignItems: 'center', children: _jsx(Typography, { style: {
                            whiteSpace: 'nowrap',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }, variant: 'body2', children: params.row.machineName }) }));
            }
        },
        {
            field: 'chatSource',
            headerName: translate('audit.chatSource'),
            flex: isXs ? undefined : isMd ? undefined : isscreen600900 ? undefined : isSm ? 1.5 : 1.5,
            minWidth: isXs ? 150 : isscreen600900 ? 150 : isMd ? 150 : undefined,
            width: isXs ? 200 : isMd ? 200 : isscreen600900 ? 200 : undefined,
            type: 'string',
            filterOperators: getFilteredStringOperators(),
            renderCell: function (params) {
                if (!params.value) {
                    return null;
                }
                return translate("common.".concat(params.value.toLowerCase()));
            }
        },
        {
            field: 'status',
            headerName: translate('common.status'),
            filterable: false,
            flex: isXs ? undefined : isMd ? undefined : isscreen600900 ? undefined : isSm ? 1.5 : 1.5,
            minWidth: isXs ? 150 : isscreen600900 ? 150 : isMd ? 150 : undefined,
            width: isXs ? 200 : isMd ? 200 : isscreen600900 ? 200 : undefined,
            type: 'string',
            renderCell: function (params) {
                if (!params.value) {
                    return null;
                }
                var color = 'default';
                var translatedLabel = params.value;
                switch (params.value) {
                    case 'Active':
                        color = 'primary';
                        translatedLabel = translate('audit.active');
                        break;
                    case 'Not Completed':
                        color = 'error';
                        translatedLabel = translate('audit.notCompleted');
                        break;
                    case 'Closed':
                        color = 'default';
                        translatedLabel = translate('audit.completed');
                        break;
                    default:
                        color = 'default';
                        break;
                }
                return _jsx(Chip, { label: translatedLabel, color: color, variant: 'soft', size: 'medium' });
            }
        },
        {
            field: 'responses',
            headerName: translate('audit.responses'),
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            flex: isXs ? undefined : isMd ? undefined : isscreen600900 ? undefined : isSm ? 1.5 : 2.5,
            minWidth: isXs ? 150 : isscreen600900 ? 150 : isMd ? 300 : undefined,
            width: isXs ? 200 : isMd ? 200 : isscreen600900 ? 200 : undefined,
            renderCell: function (params) {
                return (_jsxs(Box, { mt: 0.5, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 3, children: [_jsxs(Box, { display: 'flex', width: '60px', flexDirection: 'row', gap: 2, alignItems: 'center', children: [_jsx(CheckmarkCircle01Icon, { size: 20, color: theme.palette.primary.main }), _jsx(Typography, { variant: 'body2', children: params.row.responses.resolved })] }), _jsxs(Box, { display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center', width: '60px', children: [_jsx(ThumbsUpIcon, { color: theme.palette.success.main, size: 20 }), _jsx(Typography, { variant: 'body2', children: params.row.responses.liked })] }), _jsxs(Box, { display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center', width: '60px', children: [_jsx(ThumbsDownIcon, { color: theme.palette.error.main, size: 20 }), _jsx(Typography, { variant: 'body2', children: params.row.responses.disliked })] })] }));
            }
        },
        {
            field: 'action',
            headerName: '',
            disableColumnMenu: true,
            filterable: false,
            sortable: false,
            align: 'right',
            renderCell: function (params) {
                return (_jsx(IconButton, { color: 'primary', size: 'small', onClick: function (e) {
                        e.stopPropagation();
                        handleAuditDetails(params.row);
                    }, children: _jsx(InformationCircleIcon, { size: 20 }) }));
            }
        }
    ], false);
    return (_jsxs(Box, { sx: {
            width: '100%',
            padding: isXs ? '16px' : '8px',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative'
        }, children: [_jsxs(Box, { sx: {
                    position: 'sticky',
                    top: 59,
                    zIndex: 2,
                    backgroundColor: '#fafdff',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    paddingBottom: 2
                }, children: [_jsx(Typography, { variant: isXs ? 'h4' : 'h3', color: '#009983', children: translate('common.audit') }), _jsxs(Box, { display: 'flex', alignItems: 'center', gap: 1, children: [isScreen12001300 && _jsx(FilterButton, { variant: 'soft', onClick: handleFilterClick }), !isXs && !isIpadMini && !isIpadMiniLandscape && !isLg && (_jsx(FilterButton, { variant: 'soft', onClick: handleFilterClick })), _jsx(Button, { variant: 'contained', color: 'primary', sx: { whiteSpace: 'nowrap' }, size: isXs ? 'small' : isscreen600900 ? 'small' : 'medium', onClick: onWeekChange, endIcon: _jsx(ArrowDown2, { size: 18 }), children: translate(auditFilters.dateRange, { count: auditFilters.days }) }), _jsx(Menu, { anchorEl: daysAnchorEl, open: Boolean(daysAnchorEl), onClose: function () { return setDaysAnchorEl(null); }, slotProps: {
                                    paper: {
                                        sx: {
                                            maxHeight: '300px !important',
                                            overflowY: 'auto'
                                        }
                                    },
                                    list: {
                                        sx: {
                                            minWidth: daysAnchorEl ? daysAnchorEl.clientWidth : 'auto'
                                        }
                                    }
                                }, children: weekOptions
                                    .filter(function (_a) {
                                    var value = _a.value;
                                    return value !== auditFilters.days && value !== 0;
                                })
                                    .map(function (_a) {
                                    var value = _a.value, label = _a.label;
                                    return (_jsx(MenuItem, { onClick: function () { return onWeekClose(value, label); }, children: translate(label, { count: value }) }, value));
                                }) })] })] }), (isXs || isIpadMini || isIpadMiniLandscape) && (_jsxs(_Fragment, { children: [_jsxs(Box, { display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', position: 'relative', width: '100%', children: [_jsxs(Drawer, { open: openFilterDrawer, onClose: function () { return setOpenFilterDrawer(false); }, anchor: 'bottom', slotProps: {
                                    paper: {
                                        sx: {
                                            borderTopLeftRadius: 24,
                                            borderTopRightRadius: 24
                                        }
                                    }
                                }, children: [_jsxs(Box, { display: 'flex', justifyContent: 'space-between', padding: '24px 12px 24px 24px', alignItems: 'center', children: [_jsx(Typography, { variant: 'h6', children: translate('common.filters') }), _jsx(IconButton, { size: 'Xs', onClick: function () {
                                                    setAuditFilters(__assign(__assign({}, auditFilters), { status: [], machines: [] }));
                                                    setOpenFilterDrawer(false);
                                                }, children: _jsx(Icon, { icon: 'mingcute:close-line' }) })] }), _jsxs(Box, { px: '24px', children: [_jsxs(Grid, { container: true, spacing: { xs: 2, md: 2 }, children: [_jsx(Grid, { size: { xs: 12, lg: 3 }, children: _jsx(FormControl, { size: 'small', fullWidth: true, children: _jsx(Autocomplete, { noOptionsText: translate('common.noOptions'), size: 'small', multiple: true, options: statusList, value: statusList.filter(function (s) { return auditFilters.status.includes(s.value); }), disableCloseOnSelect: true, getOptionLabel: function (option) { return option.label; }, isOptionEqualToValue: function (option, value) { return option.value === value.value; }, renderInput: function (params) { return (_jsx(TextField, __assign({}, params, { variant: 'outlined', label: translate('common.status') }))); }, onChange: function (_, value) {
                                                                    setAuditFilters(function (prev) { return (__assign(__assign({}, prev), { status: value.map(function (v) { return v.value; }) })); });
                                                                }, renderOption: function (props, option, _a) {
                                                                    var selected = _a.selected;
                                                                    return (_createElement("li", __assign({}, props, { key: option.value, className: option.disabled ? 'disabled' : '', style: option.disabled ? { color: 'gray', pointerEvents: 'none' } : {} }),
                                                                        _jsx(Checkbox, { style: { marginRight: 8 }, checked: selected }),
                                                                        option.label));
                                                                }, renderValue: function (tagValue, getTagProps) {
                                                                    return tagValue.map(function (option, index) { return (_createElement(Chip, __assign({}, getTagProps({ index: index }), { key: option.value, label: option.label, size: 'small', variant: 'soft' }))); });
                                                                } }) }) }), _jsx(Grid, { size: { xs: 12, lg: 3 }, children: _jsx(FormControl, { size: 'small', fullWidth: true, children: _jsx(Autocomplete, { noOptionsText: translate('common.noOptions'), size: 'small', multiple: true, options: machinesList, disableCloseOnSelect: true, limitTags: 2, value: machinesList.filter(function (m) { return auditFilters.machines.includes(m.id); }), getOptionLabel: function (option) { return option.name; }, isOptionEqualToValue: function (option, value) { return option.id === value.id; }, renderInput: function (params) { return (_jsx(TextField, __assign({}, params, { variant: 'outlined', label: translate('common.assets') }))); }, onChange: function (_, value) {
                                                                    setAuditFilters(function (prev) { return (__assign(__assign({}, prev), { machines: value.map(function (v) { return v.id; }) })); });
                                                                }, renderOption: function (props, option, _a) {
                                                                    var selected = _a.selected;
                                                                    return (_createElement("li", __assign({}, props, { key: option.id }),
                                                                        _jsx(Checkbox, { style: { marginRight: 8 }, checked: selected }),
                                                                        option.name));
                                                                }, renderValue: function (tagValue, getTagProps) {
                                                                    return tagValue.map(function (option, index) { return (_createElement(Chip, __assign({}, getTagProps({ index: index }), { key: option.id, label: option.name, size: 'small', variant: 'soft' }))); });
                                                                } }) }) }), _jsx(LocalizationProvider, { dateAdapter: AdapterDayjs, children: _jsx(Grid, { size: { xs: 12, lg: 3 }, children: _jsx(DateRangePicker, { value: selectedDate, maxDate: dayjs(), onChange: function (value) {
                                                                    if (value[0] && value[1]) {
                                                                        setAuditFilters(function (prev) {
                                                                            var _a, _b, _c, _d;
                                                                            return (__assign(__assign({}, prev), { days: 0, dateRange: 'audit.customDate', startDate: (_b = (_a = value[0]) === null || _a === void 0 ? void 0 : _a.toDate()) !== null && _b !== void 0 ? _b : new Date(), endDate: (_d = (_c = value[1]) === null || _c === void 0 ? void 0 : _c.toDate()) !== null && _d !== void 0 ? _d : new Date() }));
                                                                        });
                                                                    }
                                                                } }) }) })] }), _jsxs(Box, { py: '24px', display: 'flex', gap: '12px', justifyContent: 'center', children: [_jsx(Button, { variant: 'outlined', fullWidth: true, color: 'inherit', size: 'medium', onClick: function () {
                                                            setAuditFilters(__assign(__assign({}, auditFilters), { status: [], machines: [] }));
                                                            setOpenFilterDrawer(false);
                                                        }, children: translate('actions.cancel') }), _jsx(Button, { type: 'submit', variant: 'contained', fullWidth: true, color: 'inherit', size: 'medium', onClick: function () {
                                                            filter();
                                                            setOpenFilterDrawer(false);
                                                        }, children: translate('actions.apply') })] })] })] }), tabValue === 'Audits' && (_jsxs(Box, { display: 'flex', gap: '0.5', alignItems: 'center', width: '100%', children: [_jsxs(Typography, { variant: 'subtitle2', children: [conversations.length, " ", translate('actions.items')] }), _jsx(IconButton, { size: 'Xs', onClick: function () { return setOpenSortDrawer(true); }, children: _jsx(SortByUp01Icon, {}) })] })), _jsx(Box, { display: 'flex', justifyContent: 'flex-end', width: '100%', alignItems: 'center', children: _jsx(FilterButton, { variant: 'soft', onClick: handleFilterClick }) })] }), _jsx(Box, { sx: { width: '100%' }, children: _jsxs(TabContext, { value: tabValue, children: [_jsx(Box, { sx: {
                                        display: 'flex',
                                        justifyContent: 'flex-start'
                                    }, children: _jsxs(TabList, { onChange: handleChange, "aria-label": 'Settings tabs', children: [_jsx(Tab, { label: 'Audits', value: 'Audits', sx: {
                                                    borderRadius: '16px',
                                                    color: tabValue === 'Audits' ? '#161C24 !important' : '#454F5B !important'
                                                } }), _jsx(Tab, { label: 'Trends', value: 'Trends', sx: {
                                                    borderRadius: '16px',
                                                    color: tabValue === 'Trends' ? '#161C24 !important' : '#454F5B !important'
                                                } })] }) }), _jsx(Box, { sx: { width: '100%' }, children: _jsx(TabPanel, { value: 'Audits', sx: { padding: '6px' }, children: _jsx(AuditCards, { openSortDrawer: openSortDrawer, setOpenSortDrawer: setOpenSortDrawer, audits: conversations, conversationFilters: conversationFilters, setConversationFilters: setConversationFilters, onViewAudit: handleAuditDetails }) }) }), _jsx(Box, { sx: { width: '100%' }, children: _jsx(TabPanel, { value: 'Trends', sx: { padding: '6px' }, children: _jsx(Box, { style: {
                                                maxHeight: isscreen600900Landscape
                                                    ? 'calc(100svh - 288px)'
                                                    : 'calc(100svh - 290px)',
                                                overflowY: 'auto',
                                                paddingBottom: '16px',
                                                gap: 2
                                            }, children: _jsxs(Grid, { container: true, spacing: { xs: 1, md: 2 }, children: [_jsx(Grid, { size: { xs: 12, lg: 3 }, children: _jsxs(Box, { display: 'flex', flexDirection: 'row', bgcolor: 'rgba(250, 253, 255, 1)', boxShadow: theme.customShadows.z8, padding: '20px', borderRadius: 2, gap: '4px', alignItems: 'center', justifyContent: 'space-between', children: [_jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 1, children: [_jsx(Typography, { variant: 'caption', fontWeight: 600, children: translate('audit.activeChats') }), _jsx(Typography, { variant: 'h2', children: auditData === null || auditData === void 0 ? void 0 : auditData.activeChatsCount })] }), _jsx(Box, { alignItems: 'center', display: 'flex', children: _jsx(MessageDelay02Icon, { color: theme.palette.secondary.main, width: 40, height: 40 }) })] }) }), _jsx(Grid, { size: { xs: 12, lg: 3 }, children: _jsxs(Box, { display: 'flex', flexDirection: 'row', bgcolor: 'rgba(250, 253, 255, 1)', boxShadow: theme.customShadows.z8, padding: '20px', borderRadius: 2, gap: '4px', alignItems: 'center', justifyContent: 'space-between', children: [_jsxs(Box, { display: 'flex', flexDirection: 'column', width: '50%', gap: 1, children: [_jsx(Typography, { variant: 'caption', fontWeight: 600, children: translate('audit.completedChats') }), _jsx(Typography, { variant: 'h2', children: auditData === null || auditData === void 0 ? void 0 : auditData.closedChatsCount })] }), _jsx(Box, { alignItems: 'center', display: 'flex', children: _jsx(MessageDone02Icon, { color: theme.palette.primary.main, width: 40, height: 40 }) })] }) }), _jsx(Grid, { size: { xs: 12, lg: 3 }, children: _jsxs(Box, { display: 'flex', flexDirection: 'row', bgcolor: 'rgba(250, 253, 255, 1)', boxShadow: theme.customShadows.z8, padding: '20px', borderRadius: 2, gap: '4px', alignItems: 'center', justifyContent: 'space-between', children: [_jsxs(Box, { display: 'flex', flexDirection: 'column', width: '50%', gap: 1, children: [_jsx(Typography, { variant: 'caption', fontWeight: 600, children: translate('audit.like') }), _jsx(Typography, { variant: 'h2', children: auditData === null || auditData === void 0 ? void 0 : auditData.likedCount })] }), _jsx(Box, { alignItems: 'center', display: 'flex', children: _jsx(ThumbsUpIcon, { color: theme.palette.success.main, width: 40, height: 40 }) })] }) }), _jsx(Grid, { size: { xs: 12, lg: 3 }, children: _jsxs(Box, { display: 'flex', flexDirection: 'row', bgcolor: 'rgba(250, 253, 255, 1)', boxShadow: theme.customShadows.z8, padding: '20px', borderRadius: 2, gap: '4px', alignItems: 'center', justifyContent: 'space-between', children: [_jsxs(Box, { display: 'flex', flexDirection: 'column', width: '50%', gap: 1, children: [_jsx(Typography, { variant: 'caption', fontWeight: 600, children: translate('common.feedback') }), _jsx(Typography, { variant: 'h2', children: auditData === null || auditData === void 0 ? void 0 : auditData.dislikedCount })] }), _jsx(Box, { alignItems: 'center', display: 'flex', children: _jsx(ThumbsDownIcon, { color: theme.palette.error.main, width: 40, height: 40 }) })] }) })] }) }) }) })] }) })] })), !isXs && !isIpadMini && !isIpadMiniLandscape && (_jsx(AuditDesktop, { auditData: auditData, auditFilters: auditFilters, setAuditFilters: setAuditFilters, machinesList: machinesList, statusList: statusList, selectedDate: selectedDate, filter: filter, openFilterDrawer: openFilterDrawer, setOpenFilterDrawer: setOpenFilterDrawer, filterModel: filterModel, onFilterModelChange: handleFilterModelChange, options: options, conversations: conversations, rowCount: rowCount, auditColumns: auditColumns, paginationModel: {
                    page: conversationFilters.page - 1,
                    pageSize: conversationFilters.pageSize
                }, handleAuditDetails: handleAuditDetails, handlePaginationModelChange: handlePaginationModelChange, handleSortModelChange: handleSortModelChange, isscreen9001200Landscape: isscreen9001200Landscape, iscreen9001200: iscreen9001200, isscreen600900: isscreen600900, isscreen600900Landscape: isscreen600900Landscape, isXs: isXs }))] }));
};
export default Audit;
